import { PopoverPlacement } from "@idui/react-popover";
import { IconButton } from "@mui/material";
import { startCase } from "lodash";
import { useCallback, type FC } from "react";
import { useTranslation } from "react-i18next";

import { PIC } from "@ryde/assets";
import {
  canResolveIfSimpleDtoExists,
  useDrawers,
  type IStationDto,
} from "@ryde/shared";

import { FramerTooltip } from "../../../tooltips";

interface IButtonAddStation {
  callbackFn: (newId: number) => Promise<void>;
  /** What a new station starts with in the drawer, over the station form's defaults. */
  newStationDraft?: Partial<IStationDto>;
}

export const ButtonAddStation: FC<IButtonAddStation> = ({
  callbackFn,
  newStationDraft,
}) => {
  const { t } = useTranslation();
  const { openNewOrDuplicateDrawer } = useDrawers();
  const handleInsertItem = useCallback(async (): Promise<void> => {
    const simpleResult = await openNewOrDuplicateDrawer({
      entity: "station",
      canResolve: canResolveIfSimpleDtoExists,
      newItemDraft: newStationDraft,
    });
    const newId = simpleResult.data?.id;

    if (newId) {
      await callbackFn(newId);
    }
  }, [callbackFn, newStationDraft, openNewOrDuplicateDrawer]);

  return (
    <FramerTooltip
      content={startCase(t("general.ADD_NEW", { entity: t("station.single") }))}
      placement={PopoverPlacement.top}
    >
      {/* Prevent "A disabled element does not fire events." MUI error*/}
      <span>
        <IconButton onClick={() => handleInsertItem()} color="primary">
          <PIC.PlusCircleSvg width={20} height={20} />
        </IconButton>
      </span>
    </FramerTooltip>
  );
};
